import { useCallback } from 'react';

import { Headline } from '~components/Typography';
import { annals } from '~features/annals/hooks';
import { UnsubscriptionFlowStages } from '~features/annals/names';
import { Toaster } from '~features/toaster';
import { useSubscriptions } from '~hooks/useSubscriptions';
import { useUserId } from '~hooks/useUserId';
import { useTranslations } from '~i18n/hooks';
import { subscriptionApi } from '~services/api/subscription';
import {
	DatadogLogger,
	LoggerMessages,
	LoggerServices,
} from '~shared/services/logger';
import { Loader } from '~shared/ui/atoms';
import { ReviewModal } from '~shared/ui/modals/ReviewModal';
import { ButtonSecondary } from '~shared/ui/molecules';

import { Policies } from '../Policies';
import { Row } from '../Row';
import { RedText } from './styled';

type Props = {
	onClose: () => void;
	onConfirm: () => void;
	reasons: string;
	endDate: string;
};

export const PromptModal = ({
	onClose,
	onConfirm,
	reasons,
	endDate,
}: Props) => {
	const t = useTranslations('MySubscriptions');

	const trackButtonCancelCancelSub =
		annals.subscription.useClickButtonCancelCancelSub();
	const trackButtonNotNowCancelSub =
		annals.subscription.useClickButtonNotNowCancelSub();
	const trackCloseModal = annals.subscription.useClickElementCloseCancelSub(
		UnsubscriptionFlowStages.CancelPremium
	);

	// Если мы сюда пришли, значит подписка у пользователя точно есть. И сейчас она всегда одна
	const {
		activeSubscription: { sku, externalSystem } = {
			sku: '',
			externalSystem: '',
		},
	} = useSubscriptions();

	const userId = useUserId();
	const [cancel, { isLoading }] =
		subscriptionApi.useCancelSubscriptionMutation();

	const onCancel = useCallback(
		async () =>
			cancel({
				id: userId,
				body: {
					reason: reasons,
					sku,
					externalSystem,
				},
			})
				.unwrap()
				.then(() => {
					Toaster.showSuccess({
						titleKey: 'subscriptionCanceledTitle',
					});
					onConfirm();
				})
				.catch((e) => {
					DatadogLogger.error(
						LoggerServices.PaymentService,
						LoggerMessages.CannotCancelSubscription,
						{ error: e }
					);
					Toaster.showError({
						titleKey: 'requestFailedTitle',
						textKey: 'requestFailedMessage',
					});
				}),
		[cancel, externalSystem, onConfirm, reasons, sku, userId]
	);

	return (
		<ReviewModal
			open={true}
			onClose={() => {
				trackCloseModal();
				onClose();
			}}
			locked
			title={
				<Headline size="L" align="left">
					{t('promptTitle')}
				</Headline>
			}
			subtitle={t('promptBody', { date: endDate })}
			data-name="prompt-cancel-modal"
		>
			<Row>
				{isLoading ? (
					<Loader />
				) : (
					<>
						<ButtonSecondary
							size="M"
							onClick={() => {
								trackButtonCancelCancelSub();
								void onCancel();
							}}
							fullWidth
						>
							<RedText>{t('promptContinueButton')}</RedText>
						</ButtonSecondary>
						<ButtonSecondary
							size="M"
							onClick={() => {
								trackButtonNotNowCancelSub();
								onClose();
							}}
							fullWidth
						>
							{t('promptBackButton')}
						</ButtonSecondary>
					</>
				)}
			</Row>
			<Policies screen={UnsubscriptionFlowStages.CancelPremium} />
		</ReviewModal>
	);
};
